import { tabsOf, totalsOf } from '../order-tabs/order-tabs'
import { useGetOrderTabs } from '@agos/api-client/orders'
import { getGetRevenueHandoversQueryKey, useGetRevenueHandovers } from '@agos/api-client/payments'
import {
  T,
  formatCents,
  formatCount,
  formatDay,
  formatTime,
  parseMoneyToCents,
  toDateParam,
  tt,
  type MoneyInput,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
  type Column,
  DataTable,
  DateRangePicker,
  FilterBar,
  FilterSelect,
  PageHeader,
  StatusBadge,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Banknote, Calculator, CreditCard, Hash, Minus, Receipt, RefreshCw, TrendingUp, Truck, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  auditHandover,
  driftDirection,
  filterHandovers,
  handoverTotals,
  type HandoverFilter,
  type HandoverReport,
} from './handover-report'

/**
 * `/revenue-handovers` — the shift closings on record.
 *
 * `manager-orders` owns the screen that creates one. This is the screen that
 * checks them, and checking is the part legacy does not do: it renders the stored
 * numbers and never compares them to what the register says was taken.
 *
 * FB-068 is what that misses. Across the 363 handovers in the restored data the
 * form's own arithmetic always reconciles — but in **103 of them** the card figure
 * the person entered disagrees with the wallet's own terminal-plus-courier total,
 * and each of those is cash that either was or was not handed over.
 *
 * Two additions to legacy's layout, both in service of that: a "Kontrola" column
 * saying whether a row holds together, and a filter narrowing to the rows that do
 * not. Everything else — the hero, the four payment-method cards, the sortable
 * table, the three-column detail panel below it — is legacy's, label for label.
 *
 * Ported from legacy `pages/revenue-handovers.tsx`.
 */

function today(): string {
  return toDateParam(new Date())
}

export function RevenueHandoversPage() {
  const queryClient = useQueryClient()
  // Legacy opens on today only and the operator widens it from there. Kept: the
  // default range decides what the page claims the shift owes.
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [filter, setFilter] = useState<HandoverFilter>('all')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const { data, isFetching } = useGetRevenueHandovers({ from, to })
  // §9.5 asks for the outstanding tab balance to be visible where the cash expectations are. It is read
  // here rather than computed: `/order-tabs` is the open ones, already filtered by the server.
  const openTabs = useGetOrderTabs()
  const openTabsCents = totalsOf(tabsOf(openTabs.data)).outstandingCents

  const reports: HandoverReport[] = useMemo(() => data ?? [], [data])
  const shown = useMemo(
    () => filterHandovers(reports, { filter, search }),
    [reports, filter, search],
  )
  const totals = handoverTotals(shown)

  const columns: Column<HandoverReport>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: tt('Dátum'),
        sortable: true,
        sortValue: (report) => new Date(report.createdAt ?? 0).getTime(),
        render: (report) => (
          <div>
            <div className="font-medium text-foreground">
              {report.createdAt ? formatDay(report.createdAt) : '-'}
            </div>
            <div className="text-xs text-muted-foreground">
              {report.createdAt ? formatTime(report.createdAt) : ''}
            </div>
          </div>
        ),
      },
      {
        key: 'employeeName',
        header: tt('Zamestnanec'),
        sortable: true,
        sortValue: (report) => report.employeeName,
        render: (report) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-xs shrink-0">
              {initials(report.employeeName)}
            </div>
            <span className="font-bold text-foreground text-sm truncate">{report.employeeName}</span>
          </div>
        ),
      },
      {
        key: 'totalRevenue',
        header: tt('Tržba'),
        sortable: true,
        sortValue: (report) => auditHandover(report).totalRevenueCents,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => (
          <span className="font-bold text-foreground tabular-nums">
            {formatCents(auditHandover(report).totalRevenueCents)}
          </span>
        ),
      },
      {
        key: 'cashToHandOver',
        header: tt('Hotovosť'),
        sortable: true,
        sortValue: (report) => auditHandover(report).cashToHandOverCents,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => {
          const cash = auditHandover(report).cashToHandOverCents
          return (
            <span
              className={cn(
                'text-lg font-black tabular-nums',
                cash < 0 ? 'text-red-400' : 'text-emerald-400',
              )}
            >
              {formatCents(cash)}
            </span>
          )
        },
      },
      {
        // Not in legacy. FB-068 — the reason for reviewing a handover at all.
        key: 'audit',
        // §9.177 — the owner asked what this column is, which is the answer: it did not say. The
        // header and every cell now name what was compared with what.
        header: tt('Kontrola'),
        headerTitle: tt(
          'Porovnáva zadané tržby s tým, čo hovorí pokladňa: súčet hotovosti a kariet proti celkovej sume, a zadané karty proti terminálu a kuriérom.',
        ),
        sortable: true,
        sortValue: (report) => Math.abs(auditHandover(report).cardDriftCents),
        render: (report) => {
          const audit = auditHandover(report)
          if (audit.arithmeticOff) {
            return (
              <StatusBadge
                color="red"
                label={tt('nesedí súčet')}
                icon={AlertTriangle}
                title={tt('Hotovosť a karty spolu nedávajú zadanú celkovú tržbu.')}
              />
            )
          }
          if (audit.cardDrifts) {
            return (
              <StatusBadge
                color="amber"
                label={`${tt('karty')} ${formatCents(audit.cardDriftCents)}`}
                icon={AlertTriangle}
                title={tt(
                  'Zadané karty sa líšia od súčtu terminálu a kuriérov o túto sumu - chýbajúca alebo prebytočná hotovosť.',
                )}
              />
            )
          }
          // Not an em dash: a dash reads as *not checked*, and this row was checked and is right.
          return (
            <span
              className="text-xs text-emerald-500"
              title={tt('Súčty sedia aj karty sedia s terminálom a kuriérmi.')}
            >
              {tt('sedí')}
            </span>
          )
        },
      },
      {
        key: 'orderCount',
        header: tt('Obj.'),
        sortable: true,
        sortValue: (report) => report.orderCount ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (report) => (
          <span className="text-muted-foreground font-medium tabular-nums">
            {report.orderCount ?? 0}
          </span>
        ),
      },
    ],
    [],
  )

  const expanded = shown.find((report) => report.id === expandedId)

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Odovzdanie tržby')}
        description={tt('Prehľad odovzdaných tržieb šoférov')}
        icon={<Truck className="w-5 h-5" />}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Meno, poznámka k výdaju...'),
        }}
        filters={
          <>
            <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
            <FilterSelect
              value={filter}
              label={tt('Zobraziť uzávierky')}
              onChange={(value) => setFilter(value as HandoverFilter)}
              options={[
                { value: 'all', label: tt('Všetky uzávierky') },
                { value: 'drifting', label: tt('Len nezhody s kasou') },
              ]}
            />
          </>
        }
        actions={
          <button
            type="button"
            onClick={() =>
              void queryClient.invalidateQueries({ queryKey: getGetRevenueHandoversQueryKey() })
            }
            disabled={isFetching}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm active:scale-95 transition-all disabled:opacity-50')}
          >
            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
            <T>Načítať</T>
          </button>
        }
      />

      {shown.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4" data-handover-totals>
          <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between min-w-[220px]">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">
                <T>Hotovosť na odovzdanie</T>
              </p>
              <p
                className={cn(
                  'text-5xl font-black leading-none tracking-tight tabular-nums',
                  totals.cashCents < 0 ? 'text-red-400' : 'text-emerald-400',
                )}
              >
                {formatCents(totals.cashCents)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {totals.count} {reportWord(totals.count)} · {totals.orderCount} <T>objednávok</T>
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                <T>Celková tržba</T>
              </p>
              <p className="text-xl font-semibold text-foreground tabular-nums">
                {formatCents(totals.revenueCents)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <WalletCard
              label={tt('Hotovosť')}
              cents={totals.walletCashCents}
              icon={Banknote}
              tone="emerald"
            />
            <WalletCard
              label={tt('Karta terminál')}
              cents={totals.walletTerminalCents}
              icon={CreditCard}
              tone="blue"
            />
            <WalletCard
              label={tt('Kuriér karta')}
              cents={totals.walletCourierCents}
              icon={CreditCard}
              tone="orange"
            />
            <WalletCard
              label="CardPay online"
              cents={totals.walletOnlineCents}
              icon={CreditCard}
              tone="violet"
            />
          </div>

          {/* §9.252 - the owner's item K: *"the tiles should also carry výplaty and nákupy for the
            * whole day, so the figure is the real cash."* The headline above already **is** real
            * cash — `cashToHandOver` is net of all four of these — so what was missing is what it is
            * net *of*. Every tile that was here before is still here; these sit under them, which is
            * the owner's own condition. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-handover-outgoings>
            <WalletCard
              label={tt('Výplaty')}
              cents={totals.payoutsCents}
              icon={Minus}
              tone="orange"
              testId="payouts"
            />
            <WalletCard
              label={tt('Nákupy')}
              cents={totals.purchasesCents}
              icon={Minus}
              tone="orange"
              testId="purchases"
            />
            <WalletCard
              label={tt('Tankovanie')}
              cents={totals.fuelCents}
              icon={Minus}
              tone="orange"
              testId="fuel"
            />
            <WalletCard
              label={tt('Lístky')}
              cents={totals.vouchersCents}
              icon={Minus}
              tone="orange"
              testId="vouchers"
            />
          </div>

          {/* A payout with no note is the one a week later nobody can explain. `payoutNotes` already
            * reads them; this says how many of the period's records carry one. */}
          {totals.payoutsCents > 0 && (
            <p className="text-[11px] text-muted-foreground" data-payout-notes={totals.annotatedPayouts}>
              <T>Poznámku k výplate má</T> {formatCount(totals.annotatedPayouts)} <T>z</T>{' '}
              {formatCount(totals.count)} <T>záznamov</T>.
            </p>
          )}
        </div>
      )}

      {/* §9.5: the outstanding tab balance, where the cash expectations are - with the thing about it that
        * is easy to misread. `OrderTabService`: an open tab is deliberately **not** part of what anybody
        * hands over, because the sale is not registered until the tab is paid. */}
      {openTabsCents > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-open-tabs>
          <Receipt className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Na otvorených účtoch je</T> <strong>{formatCents(openTabsCents)}</strong> -{' '}
            <T>
              do očakávanej hotovosti sa nepočíta, lebo objednávka na účte sa zaregistruje až pri úhrade.
            </T>
          </span>
        </p>
      )}

      {totals.driftingCount > 0 && (
        <p
          className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400"
          data-handover-drift
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          {/* FB-068 - each of these is cash that either was or was not handed
              over. Legacy renders the stored figures and never compares them. */}
          <span>
            <strong>
              {totals.driftingCount}/{totals.count}
            </strong>{' '}
            <T>
              uzávierok má odpočítanú sumu kariet inú, než zaznamenala kasa. Rozdiel spolu:
            </T>{' '}
            <strong>{formatCents(totals.netDriftCents)}</strong>
          </span>
        </p>
      )}

      <DataTable<HandoverReport>
        columns={columns}
        data={shown}
        pageSize={20}
        rowKey={(report) => report.id ?? 0}
        onRowClick={(report) =>
          setExpandedId(expandedId === report.id ? null : (report.id ?? null))
        }
        emptyMessage={tt('Žiadne reporty za zadané obdobie.')}
        emptyIcon={<Wallet className="w-10 h-10 mb-3 opacity-40" />}
      />

      {expanded && <HandoverDetail report={expanded} onClose={() => setExpandedId(null)} />}
    </div>
  )
}

function HandoverDetail({ report, onClose }: { report: HandoverReport; onClose: () => void }) {
  const audit = auditHandover(report)

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-handover-detail>
      <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2 min-w-0">
          <Calculator className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">
            Detail: {report.employeeName} -{' '}
            {report.createdAt
              ? `${formatDay(report.createdAt)} ${formatTime(report.createdAt)}`
              : ''}
          </span>
        </h3>
        <button
          type="button"
          onClick={onClose}
          className={cn(BUTTON_GHOST, 'text-xs shrink-0')}
        >
          <T>Zavrieť</T>
        </button>
      </div>

      <div className="px-6 pb-6 bg-muted/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
          <section className="space-y-3">
            <SectionHeading icon={TrendingUp} label={tt('Tržba podľa platby')} />
            <BreakdownRow tone="text-emerald-400" label={tt('Hotovosť')} value={report.cashTotal} />
            <BreakdownRow
              tone="text-blue-400"
              label={tt('Karta terminál')}
              value={report.cardTerminalTotal}
            />
            <BreakdownRow
              tone="text-orange-400"
              label={tt('Karta kuriér')}
              value={report.cardCourierTotal}
            />
            <BreakdownRow
              tone="text-violet-400"
              label="CardPay online"
              value={report.cardOnlineTotal}
            />
            <div className="pt-2 border-t border-border flex justify-between items-center">
              <span className="text-xs font-bold text-muted-foreground">
                <T>Celkom</T>
              </span>
              <span className="text-sm font-black text-foreground tabular-nums">
                {formatCents(audit.totalRevenueCents)}
              </span>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeading icon={Minus} label={tt('Odrátky')} />
            <BreakdownRow tone="text-violet-400" label="CardPay" value={report.cardPayAmount} />
            <BreakdownRow tone="text-blue-400" label={tt('Karta')} value={report.cardAmount} />
            <BreakdownRow
              tone="text-orange-400"
              label={tt('Lístky')}
              value={report.vouchersAmount}
            />
            <BreakdownRow
              tone="text-yellow-400"
              label={tt('Tankovanie')}
              value={report.fuelAmount}
            />
            <BreakdownRow
              tone="text-teal-400"
              label={tt('Nákup 1')}
              value={report.purchase1Amount}
            />
            {/* Legacy hides the second slots when they are zero - a handover with
                nothing bought should not read as though something was. */}
            <OptionalRow tone="text-teal-400" label={tt('Nákup 2')} value={report.purchase2Amount} />
            <OptionalRow
              tone="text-pink-400"
              label={withNote(tt('Výplata'), report.payout1Note)}
              value={report.payout1Amount}
            />
            <OptionalRow
              tone="text-pink-400"
              label={withNote(tt('Výplata 2'), report.payout2Note)}
              value={report.payout2Amount}
            />
            <div className="pt-2 border-t border-border flex justify-between items-center">
              <span className="text-xs font-bold text-muted-foreground">
                <T>Celkom odrátky</T>
              </span>
              <span className="text-sm font-black text-red-400 tabular-nums">
                -{formatCents(audit.deductionCents)}
              </span>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <SectionHeading icon={Wallet} label={tt('Výsledok')} />

            <div
              className={cn(
                'rounded-2xl p-5 border flex flex-col items-center justify-center text-center',
                audit.cashToHandOverCents < 0
                  ? 'bg-red-500/8 border-red-500/20'
                  : 'bg-emerald-500/8 border-emerald-500/20',
              )}
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <T>Hotovosť na odovzdanie</T>
              </p>
              <p
                className={cn(
                  'text-4xl font-black leading-none tabular-nums',
                  audit.cashToHandOverCents < 0 ? 'text-red-400' : 'text-emerald-400',
                )}
              >
                {formatCents(audit.cashToHandOverCents)}
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-3 text-xs space-y-1.5">
              <div className="flex justify-between text-muted-foreground">
                <span>
                  <T>Celková tržba</T>
                </span>
                <span className="text-foreground font-semibold tabular-nums">
                  {formatCents(audit.totalRevenueCents)}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>
                  <T>Odrátky</T>
                </span>
                <span className="text-red-400 font-semibold tabular-nums">
                  -{formatCents(audit.deductionCents)}
                </span>
              </div>
              <div className="border-t border-border pt-1.5 flex justify-between font-bold">
                <span className="text-muted-foreground">
                  <T>= Hotovosť</T>
                </span>
                <span
                  className={cn(
                    'tabular-nums',
                    audit.cashToHandOverCents < 0 ? 'text-red-400' : 'text-emerald-400',
                  )}
                >
                  {formatCents(audit.cashToHandOverCents)}
                </span>
              </div>
            </div>

            {audit.arithmeticOff && (
              <p className="text-xs text-red-400 leading-relaxed" data-handover-arithmetic>
                <T>Záznam nesedí - tržba mínus odrátky dáva</T>{' '}
                <strong>{formatCents(audit.expectedCashCents)}</strong>, <T>uložené je</T>{' '}
                {formatCents(audit.cashToHandOverCents)}.
              </p>
            )}

            {audit.cardDrifts && (
              <p
                className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed"
                data-handover-card-drift
              >
                {/* Spelled out in both directions: which way it went decides
                    whether the till comes up short or over. */}
                <T>Kasa zaznamenala na kartách</T> {formatCents(audit.walletCardCents)},{' '}
                <T>obsluha odpočítala</T> {formatCents(audit.enteredCardCents)} -{' '}
                {driftDirection(audit.cardDriftCents)} ({formatCents(audit.cardDriftCents)}).{' '}
                <T>
                  Záznam nemá pole pre kartu u kuriéra, takže sa buď prilepí ku karte, alebo sa
                  neodpočíta vôbec.
                </T>
              </p>
            )}

            <div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-center">
              <Hash className="w-3 h-3" />
              <span>
                {report.orderCount ?? 0} <T>objednávok</T>
              </span>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

const WALLET_TONES = {
  emerald: 'bg-emerald-500/8 border-emerald-500/20 text-emerald-400',
  blue: 'bg-blue-500/8 border-blue-500/20 text-blue-400',
  orange: 'bg-orange-500/8 border-orange-500/20 text-orange-400',
  violet: 'bg-violet-500/8 border-violet-500/20 text-violet-400',
} as const

function WalletCard({
  label,
  cents,
  icon: Icon,
  tone,
  testId,
}: {
  label: string
  cents: number
  icon: typeof Banknote
  tone: keyof typeof WALLET_TONES
  /** §9.252 — only the outgoings carry one; the four wallet tiles had none and still do not. */
  testId?: string
}) {
  return (
    <div
      className={cn('border rounded-2xl p-4 flex flex-col gap-1', WALLET_TONES[tone])}
      data-outgoing={testId}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-black leading-none tabular-nums">{formatCents(cents)}</p>
    </div>
  )
}

function SectionHeading({ icon: Icon, label }: { icon: typeof Wallet; label: string }) {
  return (
    <h4 className="text-[11px] text-muted-foreground uppercase tracking-widest font-bold flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </h4>
  )
}

function BreakdownRow({
  tone,
  label,
  value,
}: {
  tone: string
  label: string
  value: MoneyInput
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <div className={cn('w-2 h-2 rounded-full shrink-0 bg-current', tone)} />
        <span className="text-xs text-muted-foreground truncate">{label}</span>
      </div>
      <span className={cn('text-sm font-bold shrink-0 tabular-nums', tone)}>
        {formatCents(parseMoneyToCents(value))}
      </span>
    </div>
  )
}

/** A deduction slot legacy leaves out of the panel entirely when it is zero. */
function OptionalRow(props: { tone: string; label: string; value: MoneyInput }) {
  if (parseMoneyToCents(props.value) <= 0) return null
  return <BreakdownRow {...props} />
}

function withNote(label: string, note: string | null | undefined): string {
  return note && note.trim() !== '' ? `${label} (${note.trim()})` : label
}

function initials(name: string | undefined): string {
  return (name ?? '')
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
}

/**
 * `1 report`, `2 reporty`, `0` and `5+` `reportov` — Slovak counts in three, and
 * zero goes with the fives rather than the twos.
 */
function reportWord(count: number): string {
  if (count === 1) return tt('report')
  if (count >= 2 && count <= 4) return tt('reporty')
  return tt('reportov')
}
